import { useRef, useState, useEffect, useContext } from "react";
import { ChatEventManager, MessageData } from "src/components/types";
import "./GameChat.css";
import { Button, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { TableContext, TableContextType } from "../tableContext";
import { UsernameDisplay } from "src/components/pages/UserPage";
import { is } from "immer/dist/internal";

type GameChatProps = {
  eventManager: ChatEventManager;
  messages: MessageData[];
  chatOpen: boolean;
  closeChat: () => void;
  isLobby?: boolean;
};

function createFormattedMessageElement(messageData: MessageData) {
  let ownMessage = messageData.username === localStorage.getItem("user-name");
  let messageDate = new Date(messageData.timestamp ?? 0).toLocaleString();
  return (
    <div
      key={messageData.timestamp}
      className={"chat-message-wrap" + (ownMessage ? " own-message" : "") + " chat-message-" + messageData.type}
    >
      <div>
        <b className="chat-message-username">{messageData.username} </b>
        <span className="chat-date">{messageDate} </span>
      </div>
      <div className="game-chat-message">{messageData.message}</div>
    </div>
  );
}

export function GameChat({ messages, eventManager, chatOpen, closeChat, isLobby = false }: GameChatProps) {
  const [newMessage, setNewMessage] = useState<string>("");
  const [closeAfterMessage, setCloseAfterMessage] = useState<boolean>(
    localStorage.getItem("close-after-message-sent") ? true : false,
  );
  const [isLobbyChat, setIsLobbyChat] = useState<boolean>(isLobby);
  const textInputRef = useRef<HTMLDivElement>(null);

  const { connectedPlayers } = useContext(TableContext as React.Context<TableContextType>);

  var messageList = messages
    .toSorted((a, b) => (a?.timestamp ?? 0) - (b?.timestamp ?? 0))
    .map((messageData) => createFormattedMessageElement(messageData));

  function sendChatMessage() {
    let message = newMessage;
    console.log("sending chat message", message);
    eventManager.newEvent("chat message", { type: "chat message", message });
    setNewMessage("");
  }

  let messagesEndRef = useRef<HTMLDivElement>(null);
  let messagesEnd = <div ref={messagesEndRef}></div>;
  let chatMessages = (
    <div className="game-chat-messages">
      {messageList}
      {messagesEnd}
    </div>
  );

  useEffect(() => {
    scrollToEnd();
    if (chatOpen) {
      textInputRef.current?.focus();
    }
  }, [messages, chatOpen]);

  const scrollToEnd = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.parentElement?.scrollTo({
        top: messagesEndRef.current.parentElement.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  function messageKeyDown(evt: React.KeyboardEvent) {
    if (evt.key === "Enter") {
      sendChatMessage();
      evt.preventDefault();
      if (closeAfterMessage) {
        closeChat();
      }
    }
  }

  function handleCloseAfterMessageChanged(evt: React.ChangeEvent<HTMLInputElement>) {
    setCloseAfterMessage(evt.target.checked);
    if (evt.target.checked) {
      localStorage.setItem("close-after-message-sent", "true");
    } else {
      localStorage.removeItem("close-after-message-sent");
    }
  }

  return (
    <div className={isLobbyChat ? "lobby-chat" : "game-chat" + (chatOpen ? " open" : "")}>
      {chatMessages}
      {isLobbyChat ? <div className="lobby-chat-spacer" /> : null}
      <div className="chat-controls-wrapper">
        <div className="new-message-wrapper">
          <TextField
            variant="outlined"
            size="small"
            multiline
            hiddenLabel
            placeholder="Message"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={messageKeyDown}
            inputRef={textInputRef}
          />
          <Button variant="outlined" size="small" onClick={sendChatMessage}>
            Send
          </Button>
        </div>
        {isLobbyChat ? null : (
          <FormControlLabel
            control={<Checkbox checked={closeAfterMessage} onChange={(evt) => handleCloseAfterMessageChanged(evt)} />}
            label="Close chat after message is sent"
            sx={{ marginLeft: "-5px" }}
          />
        )}
        <div className="connected-players">
          Connected players:
          {[...(connectedPlayers ?? [])].map((player: string, index: number) => (
            <UsernameDisplay key={index} username={player} />
          ))}
        </div>
      </div>
    </div>
  );
}
